import React, { useCallback, useEffect, useMemo, useState } from "react";

import LoadingSpinner from "../LoadingSpinner";
import Modal from "../Modal";
import { Button } from "../ui/button";
import { useAuth } from "../../contexts/AuthContext";
import { fetchCompanies } from "../../services/adminApi";
import {
  type CustomRole,
  capLabel,
  createCustomRole,
  deleteCustomRole,
  fetchCustomRoles,
  updateCustomRole,
} from "../../services/customRolesApi";

const inputCls = "w-full border border-secondary-300 rounded px-2 py-1.5 text-sm";

const Field: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <label className="block">
    <span className="block text-xs uppercase tracking-wide text-secondary-500 mb-1">{label}</span>
    {children}
  </label>
);

/** The grantable-cap checkbox grid, shared by the create + edit forms. */
const CapChecklist: React.FC<{
  grantableCaps: string[];
  selected: Set<string>;
  onToggle: (key: string) => void;
}> = ({ grantableCaps, selected, onToggle }) => (
  <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5 border border-secondary-200 rounded p-3">
    {grantableCaps.map((key) => (
      <label key={key} className="flex items-center gap-2 text-sm">
        <input type="checkbox" checked={selected.has(key)} onChange={() => onToggle(key)} />
        <span>{capLabel(key)}</span>
      </label>
    ))}
    {grantableCaps.length === 0 && (
      <div className="text-xs text-secondary-400">No grantable capabilities.</div>
    )}
  </div>
);

/**
 * Custom Roles tab (users.manage). A customer admin manages their own company's
 * custom roles; a platform admin picks a company first, then manages that
 * company's roles. Each role bundles a set of grantable capabilities (from the
 * server whitelist) that can then be assigned to users on the Users tab.
 *
 * Capability changes take effect on a user's NEXT login (caps ride in the token).
 */
export const CustomRolesTab: React.FC = () => {
  const { token, role } = useAuth();
  const isPlatform = role === "platform";

  const [roles, setRoles] = useState<CustomRole[] | null>(null);
  const [grantableCaps, setGrantableCaps] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);

  // Platform-only company picker.
  const [companies, setCompanies] = useState<{ id: number; name: string }[]>([]);
  const [companyId, setCompanyId] = useState<number | null>(null);

  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<CustomRole | null>(null);

  // Platform users need a company list to scope both fetch + create.
  useEffect(() => {
    if (!token || !isPlatform) return;
    fetchCompanies(token)
      .then((cs) =>
        setCompanies(
          cs
            .filter((c) => c.company_type !== "platform")
            .map((c) => ({ id: c.company_id, name: c.company_name ?? `#${c.company_id}` })),
        ),
      )
      .catch((e) => setError(e instanceof Error ? e.message : String(e)));
  }, [token, isPlatform]);

  const refresh = useCallback(() => {
    if (!token) return;
    // Platform callers must pick a company before we load anything.
    if (isPlatform && companyId == null) {
      setRoles([]);
      return;
    }
    fetchCustomRoles(token, isPlatform ? companyId ?? undefined : undefined)
      .then((res) => {
        setRoles(res.roles);
        setGrantableCaps(res.grantable_caps);
      })
      .catch((e) => setError(e instanceof Error ? e.message : String(e)));
  }, [token, isPlatform, companyId]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const showRoles = !isPlatform || companyId != null;

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
        <div className="flex items-center gap-2">
          <h2 className="font-semibold text-secondary-800">Custom roles</h2>
          {isPlatform && (
            <select
              className="border border-secondary-300 rounded px-2 py-1.5 text-sm bg-white"
              value={companyId ?? ""}
              onChange={(e) => setCompanyId(e.target.value === "" ? null : Number(e.target.value))}
            >
              <option value="">Select a company…</option>
              {companies.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
          )}
        </div>
        <Button
          size="sm"
          disabled={isPlatform && companyId == null}
          onClick={() => setCreating(true)}
        >
          + New role
        </Button>
      </div>

      <p className="text-xs text-secondary-500 mb-3">
        Custom roles bundle capabilities that can then be assigned to users on the{" "}
        <strong>Users</strong> tab. Capability changes take effect on a user&apos;s{" "}
        <strong>next login</strong>.
      </p>

      {error && (
        <div className="mb-3 text-sm text-error-700 bg-error-50 px-3 py-2 rounded">{error}</div>
      )}

      {isPlatform && companyId == null ? (
        <div className="text-sm text-secondary-500 bg-secondary-50 border border-secondary-200 rounded px-3 py-4">
          Select a company to view and manage its custom roles.
        </div>
      ) : !roles ? (
        <LoadingSpinner label="loading roles" />
      ) : (
        <div className="bg-white border border-secondary-200 rounded-md overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-secondary-50 text-secondary-600 text-xs uppercase tracking-wide">
              <tr>
                <th className="text-left px-3 py-2">Role</th>
                <th className="text-left px-3 py-2">Capabilities</th>
                <th className="text-right px-3 py-2">Users</th>
                <th className="text-right px-3 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {roles.map((r) => (
                <tr key={r.role_id} className="border-t border-secondary-100 align-top">
                  <td className="px-3 py-2">
                    <div className="font-medium">{r.role_name}</div>
                    {r.description && (
                      <div className="text-xs text-secondary-500">{r.description}</div>
                    )}
                  </td>
                  <td className="px-3 py-2">
                    <div className="flex flex-wrap gap-1">
                      {r.permission_keys.length === 0 ? (
                        <em className="text-secondary-400 text-xs">none</em>
                      ) : (
                        r.permission_keys.map((k) => (
                          <span
                            key={k}
                            className="text-[11px] bg-secondary-100 text-secondary-700 border border-secondary-200 rounded px-1.5 py-0.5"
                          >
                            {capLabel(k)}
                          </span>
                        ))
                      )}
                    </div>
                  </td>
                  <td className="px-3 py-2 text-right text-secondary-600">{r.user_count}</td>
                  <td className="px-3 py-2 text-right whitespace-nowrap">
                    <button
                      type="button"
                      className="text-xs text-primary-600 hover:underline"
                      onClick={() => setEditing(r)}
                    >
                      edit
                    </button>
                  </td>
                </tr>
              ))}
              {roles.length === 0 && (
                <tr>
                  <td colSpan={4} className="px-3 py-6 text-center text-secondary-500">
                    No custom roles yet.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {creating && showRoles && (
        <RoleModal
          token={token ?? ""}
          grantableCaps={grantableCaps}
          companyId={isPlatform ? companyId ?? undefined : undefined}
          onClose={() => setCreating(false)}
          onDone={() => {
            setCreating(false);
            refresh();
          }}
        />
      )}
      {editing && (
        <RoleModal
          token={token ?? ""}
          grantableCaps={grantableCaps}
          existing={editing}
          onClose={() => setEditing(null)}
          onDeleted={() => {
            setEditing(null);
            refresh();
          }}
          onDone={() => {
            setEditing(null);
            refresh();
          }}
        />
      )}
    </div>
  );
};

/** Create + edit share one modal. `existing` present => edit mode. */
const RoleModal: React.FC<{
  token: string;
  grantableCaps: string[];
  /** Create mode (platform): the target company. */
  companyId?: number;
  existing?: CustomRole;
  onClose: () => void;
  onDone: () => void;
  onDeleted?: () => void;
}> = ({ token, grantableCaps, companyId, existing, onClose, onDone, onDeleted }) => {
  const isEdit = !!existing;
  const [name, setName] = useState(existing?.role_name ?? "");
  const [description, setDescription] = useState(existing?.description ?? "");
  const [selected, setSelected] = useState<Set<string>>(
    () => new Set(existing?.permission_keys ?? []),
  );
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const toggle = (key: string): void =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  const orderedCaps = useMemo(() => {
    // Show grantable caps in the whitelist order, but include any the role
    // already holds even if they've since dropped off the whitelist.
    const extra = (existing?.permission_keys ?? []).filter((k) => !grantableCaps.includes(k));
    return [...grantableCaps, ...extra];
  }, [grantableCaps, existing]);

  const save = async (): Promise<void> => {
    if (!name.trim()) return setErr("Role name is required.");
    setBusy(true);
    setErr(null);
    try {
      const permission_keys = Array.from(selected);
      if (isEdit && existing) {
        await updateCustomRole(token, existing.role_id, {
          name: name.trim(),
          description: description.trim() || null,
          permission_keys,
        });
      } else {
        await createCustomRole(token, {
          name: name.trim(),
          description: description.trim() || null,
          permission_keys,
          ...(companyId != null ? { company_id: companyId } : {}),
        });
      }
      onDone();
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
      setBusy(false);
    }
  };

  const remove = async (): Promise<void> => {
    if (!existing || !onDeleted) return;
    if (
      !window.confirm(
        `Delete role “${existing.role_name}”? This unassigns it from ${existing.user_count} ` +
          `user${existing.user_count === 1 ? "" : "s"}; they revert to no role until reassigned.`,
      )
    )
      return;
    setBusy(true);
    setErr(null);
    try {
      await deleteCustomRole(token, existing.role_id);
      onDeleted();
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
      setBusy(false);
    }
  };

  return (
    <Modal
      open
      onClose={onClose}
      title={isEdit ? `Edit ${existing?.role_name ?? "role"}` : "New custom role"}
      widthClass="max-w-lg"
    >
      <div className="space-y-3">
        {err && <div className="text-sm text-error-700 bg-error-50 px-3 py-2 rounded">{err}</div>}
        <Field label="Role name">
          <input
            className={inputCls}
            value={name}
            onChange={(e) => setName(e.target.value)}
            autoFocus
          />
        </Field>
        <Field label="Description (optional)">
          <input
            className={inputCls}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />
        </Field>
        <div>
          <span className="block text-xs uppercase tracking-wide text-secondary-500 mb-1">
            Capabilities
          </span>
          <CapChecklist grantableCaps={orderedCaps} selected={selected} onToggle={toggle} />
        </div>

        <div className="flex justify-between items-center gap-2 pt-1">
          <div>
            {isEdit && onDeleted && (
              <Button variant="outline" size="sm" disabled={busy} onClick={() => void remove()}>
                Delete
              </Button>
            )}
          </div>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={onClose}>
              Cancel
            </Button>
            <Button size="sm" disabled={busy} onClick={() => void save()}>
              {busy ? "Saving…" : isEdit ? "Save" : "Create role"}
            </Button>
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default CustomRolesTab;
